/**
 * FetchedMessage — NativeFetchedMessage plus parsed-header state and convenience getters.
 *
 * Inherits the NativeFetchedMessage shape (via `implements`) so a FetchedMessage
 * is-a NativeFetchedMessage: it can be passed anywhere the native interface is
 * expected. Callers that want the raw shape treat it as NativeFetchedMessage;
 * callers that want extras read `.ymd`, `.listUnsubscribe`, etc. directly.
 */

import type { NativeFetchedMessage } from "./imap-native.js";
import type { AddressData } from "./imap-protocol.js";

/** Case-insensitive multi-value header map (one key may repeat). */
class HeaderMap {
    private items: Record<string, string[]> = {};
    add(key: string, value: string): void {
        const k = key.toLowerCase();
        (this.items[k] ||= []).push(value);
    }
    get(key: string): string[] | undefined {
        return this.items[key.toLowerCase()];
    }
}

export class FetchedMessage implements NativeFetchedMessage {
    // Native fields — populated by Object.assign in the constructor.
    // `implements NativeFetchedMessage` forces this list to stay in sync.
    seq!: number;
    uid!: number;
    flags!: Set<string>;
    date!: Date | null;
    sentDate?: Date;
    subject!: string;
    messageId!: string;
    from!: AddressData[];
    to!: AddressData[];
    cc!: AddressData[];
    bcc!: AddressData[];
    sender!: AddressData[];
    replyTo!: AddressData[];
    inReplyTo!: string;
    size!: number;
    source!: string;
    headers!: string;
    seen!: boolean;
    flagged!: boolean;
    answered!: boolean;
    draft!: boolean;
    modSeq?: number;

    // Parsed-header state
    parsedHeader: string[][];
    headerSet: HeaderMap;

    constructor(init: NativeFetchedMessage) {
        Object.assign(this, init);

        // RFC 5322 unfold: a line starting with whitespace continues the prior one.
        const unfolded = (init.headers || "").replace(/\r\n(\s)/g, "$1");
        this.parsedHeader = unfolded
            .split(/\r?\n/)
            .filter(line => line.trim() !== "")
            .map(line => {
                const i = line.indexOf(":");
                return i < 0 ? [line, ""] : [line.slice(0, i), line.slice(i + 1).trim()];
            });
        this.headerSet = new HeaderMap();
        for (const [k, v] of this.parsedHeader) this.headerSet.add(k, v);
    }

    // Flag convenience
    get unread() { return !this.seen; }
    get deleted() { return this.flags.has("\\Deleted"); }
    get hasFlag() { return this.flags.size > 0; }

    // Display
    get ymd(): string | undefined { return this.date?.toLocaleDateString("se"); }
    /** Self-typed as a keyable record for dynamic address-field access by screening rules. */
    get tcsb(): Record<string, AddressData[]> {
        return this as unknown as Record<string, AddressData[]>;
    }

    // Parsed-header lookups
    get listUnsubscribe() { return this.headerSet.get("List-Unsubscribe")?.[0]; }
    get xpriority() { return this.headerSet.get("X-Priority")?.[0]; }
    get importance() { return this.headerSet.get("Importance")?.[0]; }
    get deliveredTo() { return this.headerSet.get("Delivered-To")?.[0]; }
}
